import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Check, ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface WeekStepperProps {
  weekOf: Date;
  weekStartDay: number; // 0=Sun ... 6=Sat
  onSelect: (weekOf: Date) => void;
  className?: string;
  minYear?: number;
  /**
   * When true, renders without its own card chrome (border / rounded
   * background) so it integrates cleanly inside a parent toolbar shell
   * that already provides those visuals.
   */
  flat?: boolean;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  const next = startOfDay(date);
  next.setDate(next.getDate() + days);
  return next;
}

function getWeekStart(date: Date, weekStartDay: number): Date {
  const normalizedStartDay = ((weekStartDay % 7) + 7) % 7;
  const base = startOfDay(date);
  const diff = (base.getDay() - normalizedStartDay + 7) % 7;
  return addDays(base, -diff);
}

function formatWeekLabel(start: Date, end: Date): string {
  if (start.getFullYear() !== end.getFullYear()) {
    return `${format(start, "MMM d, yyyy")} – ${format(end, "MMM d, yyyy")}`;
  }
  if (start.getMonth() !== end.getMonth()) {
    return `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`;
  }
  return `${format(start, "MMM d")} – ${format(end, "d, yyyy")}`;
}

interface WeekOption {
  value: string;
  year: number;
  weekNumber: number;
  start: Date;
  end: Date;
  disabled: boolean;
}

function getFiscalYearStart(year: number, weekStartDay: number): Date {
  return getWeekStart(new Date(year, 0, 1), weekStartDay);
}

function getFiscalWeekMeta(date: Date, weekStartDay: number): { year: number; weekNumber: number; start: Date } {
  const start = getWeekStart(date, weekStartDay);
  let year = start.getFullYear();
  const thisYearStart = getFiscalYearStart(year, weekStartDay);
  const nextYearStart = getFiscalYearStart(year + 1, weekStartDay);

  if (start < thisYearStart) {
    year -= 1;
  } else if (start >= nextYearStart) {
    year += 1;
  }

  const fiscalYearStart = getFiscalYearStart(year, weekStartDay);
  const daysSinceYearStart = Math.round((start.getTime() - fiscalYearStart.getTime()) / 86_400_000);
  return {
    year,
    weekNumber: Math.floor(daysSinceYearStart / 7) + 1,
    start,
  };
}

function buildWeekOptions(year: number, weekStartDay: number, currentWeekStart: Date): WeekOption[] {
  const firstWeekStart = getFiscalYearStart(year, weekStartDay);
  const nextYearStart = getFiscalYearStart(year + 1, weekStartDay);
  const weeks: WeekOption[] = [];

  for (let start = firstWeekStart, weekNumber = 1; start < nextYearStart; start = addDays(start, 7), weekNumber++) {
    if (start > currentWeekStart) break;
    const end = addDays(start, 6);
    weeks.push({
      value: `${year}-${weekNumber}`,
      year,
      weekNumber,
      start,
      end,
      disabled: false,
    });
  }

  return weeks.reverse();
}

function firstEnabledWeek(weeks: WeekOption[]): WeekOption | undefined {
  for (let i = 0; i < weeks.length; i++) {
    if (!weeks[i].disabled) return weeks[i];
  }
  return undefined;
}

export function WeekStepper({
  weekOf,
  weekStartDay,
  onSelect,
  className,
  minYear = 2022,
  flat = false,
}: WeekStepperProps) {
  const weekStart = getWeekStart(weekOf, weekStartDay);
  const today = startOfDay(new Date());
  const currentWeekStart = getWeekStart(today, weekStartDay);
  const currentMeta = getFiscalWeekMeta(today, weekStartDay);
  const selectedMeta = getFiscalWeekMeta(weekStart, weekStartDay);
  const selectedYear = Math.min(Math.max(selectedMeta.year, minYear), currentMeta.year);
  const years = Array.from(
    { length: currentMeta.year - minYear + 1 },
    (_, i) => minYear + i,
  ).reverse();
  const weekGroups = years.map((year) => ({
    year,
    weeks: buildWeekOptions(year, weekStartDay, currentWeekStart),
  }));
  const selectedYearWeeks = weekGroups.find((group) => group.year === selectedYear)?.weeks ?? [];
  const fallbackWeek = firstEnabledWeek(selectedYearWeeks) ?? selectedYearWeeks[0];
  const selectedWeek = selectedYearWeeks.find((week) => week.weekNumber === selectedMeta.weekNumber && !week.disabled)
    ?? fallbackWeek;
  const [open, setOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(selectedYear);
  const pickerWeekOptions = buildWeekOptions(pickerYear, weekStartDay, currentWeekStart);

  useEffect(() => {
    setPickerYear(selectedYear);
  }, [selectedYear, weekStartDay]);

  const handleWeekSelect = (week: WeekOption) => {
    if (week.disabled) return;
    onSelect(week.start);
    setOpen(false);
  };

  return (
    <div
      className={cn(
        "inline-flex items-center",
        className,
      )}
      data-testid="week-stepper"
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex h-full min-w-[210px] items-center justify-between gap-3 px-3 py-2 text-left ring-offset-background transition-colors hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              flat ? "bg-transparent" : "rounded-md border border-input bg-card"
            )}
            data-testid="week-stepper-trigger"
          >
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">
                {selectedWeek ? `${selectedWeek.year} · Week ${selectedWeek.weekNumber}` : "Select week"}
              </span>
              {selectedWeek && (
                <span className="block truncate text-xs font-normal text-muted-foreground">
                  {formatWeekLabel(selectedWeek.start, selectedWeek.end)}
                </span>
              )}
            </span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="filter-popout-glow w-[320px] p-0"
          align="start"
        >
          <div className="border-b border-border p-3">
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Year</p>
              <Select value={String(pickerYear)} onValueChange={(value) => setPickerYear(Number(value))}>
                <SelectTrigger className="h-9" data-testid="week-stepper-year">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent className="filter-popout-glow">
                  {years.map((year) => (
                    <SelectItem key={year} value={String(year)}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="max-h-[280px] overflow-y-auto p-1">
            {pickerWeekOptions.map((week) => {
              const isSelected = selectedWeek?.value === week.value;
              return (
                <button
                  key={week.value}
                  type="button"
                  disabled={week.disabled}
                  onClick={() => handleWeekSelect(week)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm outline-none transition-colors",
                    week.disabled
                      ? "cursor-not-allowed opacity-50"
                      : "hover:bg-accent hover:text-accent-foreground",
                    isSelected && "bg-accent text-accent-foreground",
                  )}
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    {isSelected && <Check className="h-4 w-4" />}
                  </span>
                  <span>
                    <span className="font-medium">Week {week.weekNumber}</span>
                    <span className="text-muted-foreground"> · {formatWeekLabel(week.start, week.end)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
